import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { InfiniteScrollCustomEvent, IonButton, IonContent, IonHeader, IonInfiniteScroll, IonInfiniteScrollContent, IonItem, IonLabel, IonList, IonRadio, IonRadioGroup, IonSearchbar, IonTitle, IonToolbar, ModalController, RadioGroupCustomEvent, SearchbarCustomEvent } from '@ionic/angular';
import { BehaviorSubject, combineLatest, firstValueFrom, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import {
    ApplicationContextStore,
    AuthService,
    BranchService,
    StorageKeys,
    StorageService,
    ToastService,
    TokenService,
    TranslationPipe,
    URLService,
    UserService,
} from '@raptor-fe/services';
import { AccessTokenDetails, Branch, SearchCriteria } from '@raptor-fe/core';

enum Role {
    SAVE = 'save',
    CANCEL = 'cancel',
}

@Component({
    selector: 'lib-modal',
    template: `
      <ion-header>
            <ion-toolbar>
                <ion-title>{{ { feature: "Mobile App", component: "Login", label: "Change Branch" } | translate }}</ion-title>
                @if (selectedBranch && selectedBranch.name !== '') {
                <ion-button
                    (click)="closeModal()"
                    fill="outline"
                    slot="start"
                    size="small"
                    >{{ { feature: "Common", component: "Button", label: "Cancel" } | translate }}
                </ion-button>
                }
                <ion-button
                    fill="solid"
                    slot="end"
                    size="small"
                    [disabled]="saveButtonDisabled()"
                    (click)="saveBranch()"
                >
                    {{ { feature: "Common", component: "Button", label: "Confirm" } | translate }}
                </ion-button>
            </ion-toolbar>

            <ion-toolbar>
                <ion-searchbar
            placeholder="Search branch"
            type="search"
            (ionInput)="search($event)" [debounce]="200"
            ></ion-searchbar>
            </ion-toolbar>
            @if (selectedBranch && selectedBranch.name !== '') {
            <ion-label class="mx-3"
            >{{ { feature: "Mobile App", component: "Login", label: "Current Branch" } | translate }}
            </ion-label>
            <ion-item class="mx-3">
                <ion-label>{{ selectedBranch.name }}</ion-label>
            </ion-item>
            }
        </ion-header>
      <ion-content role="feed">
        <ion-list >
          <ion-radio-group (ionChange)="checked($event)">
            <ion-label>{{ { feature: "Mobile App", component: "Login", label: "Available Branches" } | translate }}</ion-label>
            @for (entry of availableBranches$ | async; track entry) {
              <ion-item>
                <ion-radio [value]="entry">
                  {{ entry.name }}
                </ion-radio>
              </ion-item>
            }
          </ion-radio-group>
        </ion-list>
        <ion-infinite-scroll [disabled]="isBranchInfiniteScrollDisabled()" (ionInfinite)="onIonInfinite($event)">
          <ion-infinite-scroll-content></ion-infinite-scroll-content>
        </ion-infinite-scroll>
      </ion-content>
    `,
    styles: [
        `
            ion-content {
                --padding-start: 3%;
                --padding-end: 3%;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IonButton, IonContent, IonHeader, IonInfiniteScroll, IonInfiniteScrollContent, IonItem, IonLabel, IonList, IonRadio, IonRadioGroup, IonSearchbar, IonTitle, IonToolbar, TranslationPipe, AsyncPipe],
})
export class SelectBranchModalComponent implements OnInit, OnDestroy {
    private currentBranchPage = 1;
    private pageSize = 25;

    private activeChecked: Branch | null = null;
    private destroy$ = new Subject<void>();
    private paginatedBranches$ = new BehaviorSubject<Branch[]>([]);

    protected selectedBranch: Branch | null = null;
    protected saveButtonDisabled = signal(false);

    protected searchText$ = new BehaviorSubject<string>('');

    /**
     * Represents whether the cancel button is displayed in the UI.
     */
    protected isCancelButtonShown = input.required<boolean>();

    /**
     * Displays a modal to select a branch and handles the modal result.
     *
     * @param {ModalController} modalController - The ModalController used to create and manage the modal.
     * @param {boolean} isCancelButtonShown - A flag indicating whether the cancel button should be displayed in the modal.
     * @return {Promise<Branch | undefined>} A promise that resolves with a selected Branch object if the save role is triggered,
     * or undefined if the cancel role is triggered.
     */
    public static async show(modalController: ModalController, isCancelButtonShown: boolean): Promise<Branch | undefined> {
        const modal = await modalController.create({
            component: SelectBranchModalComponent,
            componentProps: { isCancelButtonShown: isCancelButtonShown },
        });
        await modal.present();

        const { data, role } = await modal.onWillDismiss();
        if (role === Role.SAVE) {
            return data;
        }
        if (role === Role.CANCEL) {
            return undefined;
        }
        return undefined;
    }

    /**
     * A reactive signal that represents the state of the infinite scroll feature for a branch.
     * When set to `true`, infinite scroll functionality is disabled.
     * When set to `false`, infinite scroll functionality is enabled.
     */
    protected isBranchInfiniteScrollDisabled = signal(false);

    /**
     * Request for new branches everytime the searchtext is changed,
     * when branches are found updates the list of paginated branches.
     */
    protected searchBranches$ = this.searchText$.pipe(
        distinctUntilChanged(),
        switchMap((value) => {
            const searchCriteria: SearchCriteria = {
                pageNumber: this.currentBranchPage,
                pageSize: this.pageSize,
                items: [{ name: 'name', value: value, condition: 'and', operator: 'like' }],
                orderBy: 'name',
            };

            return this.branchService.search(this.applicationContextStore.tenantId(), searchCriteria).pipe(
                map((response) => {
                    if (Array.isArray(response)) {
                        const selectedBranch = response.find((b) => b.id === this.applicationContextStore.branch()?.id) ?? response[0];
                        if (selectedBranch) {
                            this.selectedBranch = selectedBranch;
                            this.activeChecked = selectedBranch;
                            this.saveButtonDisabled.set(false);
                        }

                        this.paginatedBranches$.next(response);
                        return response;
                    }
                    return [];
                }),
            );
        }),
    );

    /**
     * Combines the latest searched branches, and the latest paginated branches,
     * if branches have been search will only return the searched branches. Else it will return the paginated branches.
     *
     * Combines latest searchBranches as a way of subscribing to it.
     */
    protected availableBranches$ = combineLatest([this.paginatedBranches$.pipe(startWith([])), this.searchBranches$.pipe(startWith([]))]).pipe(
        map(([paginatedBranches]) => {
            return paginatedBranches.filter((x) => x.id !== this.selectedBranch?.id);
        }),
    );

    private readonly branchService = inject(BranchService);
    private readonly modalController = inject(ModalController);
    private readonly storage = inject(StorageService);
    private readonly toastService = inject(ToastService);
    private readonly userService = inject(UserService);
    private readonly authService = inject(AuthService);
    private readonly tokenService = inject(TokenService);
    private readonly urlService = inject(URLService);
    private readonly applicationContextStore = inject(ApplicationContextStore);

    ngOnInit(): void {
        this.saveButtonDisabled.set(true);
        this.storage.getObject<Branch>(StorageKeys.SELECTED_BRANCH).then((val) => {
            this.selectedBranch = val;
        });
    }

    search(event: SearchbarCustomEvent): void {
        this.currentBranchPage = 1;
        this.searchText$.next(event.target.value ?? '');
    }

    closeModal(): void {
        this.modalController.dismiss(null, Role.CANCEL);
    }

    checked(e: RadioGroupCustomEvent): void {
        this.activeChecked = e.detail.value;
        this.saveButtonDisabled.set(false);
    }

    async saveBranch(): Promise<void> {
        if (this.activeChecked !== null) this.applicationContextStore.setBranch(this.activeChecked);
        this.selectedBranch = this.activeChecked;

        //Set the new current branch for the user
        await firstValueFrom(
            this.userService.setUserBranchId(this.applicationContextStore.tenantId(), this.selectedBranch?.id ?? '').pipe(
                catchError(async (error) => {
                    await this.toastService.showError(error?.message ?? 'Error changing user branch. Please try again.');
                }),
            ),
        );

        await firstValueFrom(
            this.authService.refreshToken(this.tokenService.getRefreshToken() ?? '').pipe(
                switchMap(async (res) => {
                    if ((res as AccessTokenDetails)?.access_token) {
                        const openToken = this.authService.getDecodedAccessToken((res as AccessTokenDetails).access_token as string);
                        this.applicationContextStore.setRoles(openToken?.role ?? []);
                        this.applicationContextStore.setBranch(this.selectedBranch as Branch);
                        if (this.selectedBranch?.serverUrl) {
                            this.urlService.saveURL(this.selectedBranch.serverUrl);
                        }
                    }
                }),
                catchError(async (error) => {
                    await this.toastService.showError(error?.message ?? 'Error refreshing token. Please try again.');
                }),
            ),
        );

        this.modalController.dismiss(this.activeChecked, Role.SAVE);
    }

    onIonInfinite(event: InfiniteScrollCustomEvent): void {
        this.currentBranchPage++;
        this.fetchBranches(event);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Fetches a paginated amount of branches, if a scroll event is provided will complete the event upon getting a response.
     *
     * @param event - The optional scroll event to complete.
     */
    private fetchBranches(event?: InfiniteScrollCustomEvent): void {
        this.searchText$
            .pipe(
                switchMap((value) => {
                    const searchCriteria: SearchCriteria = {
                        items: [{ name: 'name', value: value, condition: 'and', operator: 'like' }],
                        pageNumber: this.currentBranchPage,
                        pageSize: this.pageSize,
                    };
                    return this.branchService.search(this.applicationContextStore.tenantId(), searchCriteria);
                }),
                takeUntil(this.destroy$),
                withLatestFrom(this.paginatedBranches$),
                // Only want the most recent searchText before we complete the request
                // So we take(1) or else this won't stop listening.
                take(1),
            )
            .subscribe(([branches, availableBranches]) => {
                if (branches.length > 0) {
                    this.paginatedBranches$.next([...availableBranches, ...branches]);
                } else this.isBranchInfiniteScrollDisabled.set(true);
                event?.target.complete();
            });
    }
}
